// @react
import React, { ReactElement } from 'react'
// @libraries
import { makeStyles } from '@material-ui/core/styles'
import { useLocation } from 'react-router-dom'
import { useMediaQuery } from '@material-ui/core'
// @common
import config from 'constants/config'
import { Route } from 'constants/types'
// @components
import colors from '@bit/atd.web.colors'
import theme from '@bit/atd.web.theme'
import SectionMenu from '@bit/atd.web.section-menu'
import Accordion from '@bit/atd.web.accordion'
import TextLink from '@bit/atd.web.text-link'
import Grid from '@bit/atd.web.grid'
import { PropsType as ButtonProps } from '@bit/atd.web.button-nav'

/**
 * 5.0.0.1 Profile - Navigation
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=2258%3A4687
 *
 */

export const useStyles = makeStyles({
  dropdown: {
    position: 'relative',
    overflowX: 'hidden',
    '& > div': {
      borderTop: 'none',
      borderBottom: '1px solid #DBD9D6',
    },
    '& > div.Mui-expanded > div:first-child': {
      borderBottom: '1px solid #DBD9D6',
    },
    '& .MuiAccordionDetails-root': {
      padding: 0,
    },
  },
  dropdownItem: {
    position: 'relative',
    '&:not(:last-child)::after': {
      content: '""',
      borderBottom: '1px solid #DBD9D6',
      display: 'block',
      transform: 'translate(16px)',
      height: '1px',
      width: '100%',
      zIndex: 0,
    },
    '& > a': {
      background: '#F4F4F4',
      display: 'flex',
      padding: '12px 0 12px 16px',
      zIndex: 1,
      '&:hover': {
        background: `${colors.ctaPrimaryHover}`,
        color: `${colors.black}`,
      },
      '&:focus': {
        background: `${colors.ctaPrimaryHover}`,
        outline: `none`,
        boxShadow: `0 0 0 1px ${colors.ctaPrimary}`,
      },
    },
  },
})

type Link = Pick<
  ButtonProps,
  'label' | 'href' | 'active' | 'disabled' | 'linkType'
>

type PropsType = {
  routeGroup: Route[]
  user?: any
}
const SectionMenuWrapper = (props: PropsType): ReactElement => {
  /**********************
   * PROPS
   **********************/

  const { routeGroup, user } = props

  /**********************
   * HOOKS
   **********************/

  const classes = useStyles()
  const isMobile = useMediaQuery(
    theme.breakpoints.down('sm'),
    config.mediaQuery,
  )
  const location = useLocation()

  /**********************
   * FUNCTIONS
   **********************/

  const transposedRouteGroup: Link[] = routeGroup
    .filter((route) => {
      if (route.isHidden) {
        return !route.isHidden(user)
      }
      return true
    })
    .map((item) => {
      return {
        label: item.title,
        href: item.path,
        disabled: false,
        active: location.pathname === item.path,
        linkType: item.type,
      } as Link
    })

  /**
   * find the current route
   */
  const currentRoute = transposedRouteGroup.find(
    (item) => item.href === location.pathname,
  )

  /**
   *  filter out the current route
   */
  const restOfTheRoutes = transposedRouteGroup.reduce((acc: any, curr: any) => {
    if (curr.href !== location.pathname) acc.push(curr)
    return acc
  }, [])

  /**********************
   * RENDER
   **********************/

  const mobileMenu = (
    <Grid container direction={'column'}>
      {restOfTheRoutes?.map((item: any, index: number) => {
        return (
          <Grid item key={index} className={classes.dropdownItem}>
            <TextLink href={item.href} variant={'secondary'} size={'large'}>
              {item.label}
            </TextLink>
          </Grid>
        )
      })}
    </Grid>
  )

  return isMobile ? (
    <>
      <Accordion
        className={classes.dropdown}
        iconSize={25}
        panels={[
          {
            title: <h3>{currentRoute?.label}</h3>,
            content: mobileMenu,
          },
        ]}
      />
    </>
  ) : (
    <SectionMenu routeGroup={transposedRouteGroup} />
  )
}

export default SectionMenuWrapper
